<template>
  <View
    align="center"
    justify="center"
    :shrink="0"
    :class="['icon-tile', size, shape, color, { colored: coloredIcon }]"
  >
    <Icon class="icon" :name="icon" :size="size" />
  </View>
</template>

<script setup lang="ts">
import { IconName } from '../../values/icons.const';
import Icon from '../icon.vue';
import View from '../view/view.vue';
import { IconTileColor, IconTileShape, IconTileSize } from './icon-tile.props';

withDefaults(
  defineProps<{
    icon: IconName;
    size?: IconTileSize;
    shape?: IconTileShape;
    color: IconTileColor;
    coloredIcon?: boolean;
  }>(),
  {
    size: 'medium',
    shape: 'square',
  },
);
</script>

<style scoped lang="scss">
.icon-tile {
  &.large {
    width: 5rem;
    height: 5rem;
  }

  &.medium {
    width: 3rem;
    height: 3rem;
  }

  &.small {
    width: 2.25rem;
    height: 2.25rem;
  }

  &.extra-small {
    width: 1.5rem;
    height: 1.5rem;
  }

  &.square {
    border-radius: var(--t-border-radius-sm);
  }

  &.rounded {
    border-radius: var(--t-border-radius-full-rounded);
  }

  &.blue {
    background-color: var(--t-color-blue-200);

    &.colored {
      background-color: var(--t-color-blue-100);

      .icon {
        color: var(--t-color-blue-500);
      }
    }
  }

  &.violet {
    background-color: var(--t-color-violet-200);

    &.colored {
      background-color: var(--t-color-violet-100);

      .icon {
        color: var(--t-color-violet-600);
      }
    }
  }

  &.yellow {
    background-color: var(--t-color-yellow-200);

    &.colored {
      background-color: var(--t-color-yellow-100);

      .icon {
        color: var(--t-color-yellow-500);
      }
    }
  }

  &.orange {
    background-color: var(--t-color-orange-200);

    &.colored {
      background-color: var(--t-color-orange-100);

      .icon {
        color: var(--t-color-orange-400);
      }
    }
  }

  &.green {
    background-color: var(--t-color-green-200);

    &.colored {
      background-color: var(--t-color-green-100);

      .icon {
        color: var(--t-color-green-500);
      }
    }
  }

  &.neutral {
    background-color: var(--t-color-neutral-200);

    &.colored {
      background-color: var(--t-color-neutral-100);

      .icon {
        color: var(--t-color-neutral-600);
      }
    }
  }

  &.red {
    background-color: var(--t-color-red-200);

    &.colored {
      background-color: var(--t-color-red-100);

      .icon {
        color: var(--t-color-red-600);
      }
    }
  }

  &.teal {
    background-color: var(--t-color-teal-200);

    &.colored {
      background-color: var(--t-color-teal-100);

      .icon {
        color: var(--t-color-teal-500);
      }
    }
  }

  &.pink {
    background-color: var(--t-color-pink-200);

    &.colored {
      background-color: var(--t-color-pink-100);

      .icon {
        color: var(--t-color-pink-500);
      }
    }
  }

  &.pink-dust {
    background-color: var(--t-color-pink-dust-200);

    &.colored {
      background-color: var(--t-color-pink-dust-100);

      .icon {
        color: var(--t-color-pink-dust-500);
      }
    }
  }

  &.blue-dust {
    background-color: var(--t-color-blue-dust-200);

    &.colored {
      background-color: var(--t-color-blue-dust-100);

      .icon {
        color: var(--t-color-blue-dust-700);
      }
    }
  }

  &.olive {
    background-color: var(--t-color-olive-200);

    &.colored {
      background-color: var(--t-color-olive-100);

      .icon {
        color: var(--t-color-olive-500);
      }
    }
  }

  .icon {
    margin-inline: auto;
  }
}
</style>
